import React, { FC } from "react";
import { BorderedCard } from "@/common/components/BorderedCard/bordered-card";
import { SummaryRow, SummaryRows } from "@/common/components/SummaryRows/summary-rows";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { formatSavedAddress } from "@/pages/Replacement/steps/ChooseDelivery/address.mapper";
import { useDeviceOptions } from "@/pages/Replacement/steps/ChooseDevice/hooks/use-device-options.hook";
import { NO_KEYBOARD_LAYOUT } from "@/pages/Replacement/replacement.types";
import { useReplacementDraftStore } from "@/pages/Replacement/use-replacement-draft-store";
import type { ReplacementOrderResult } from "@/services/replacement-order/replacement-order.types";
import { CONFIRMATION_STRING_KEYS } from "./confirmation.constants";

const CHOOSE_DELIVERY_STRINGS = "replacement.chooseDelivery";

export interface ConfirmationSummaryProps {
  assetId: string;
  category: string;
  orderResult: ReplacementOrderResult;
}

export const ConfirmationSummary: FC<ConfirmationSummaryProps> = ({
  assetId,
  category,
  orderResult,
}) => {
  const { options } = useDeviceOptions({ assetId, category });
  const { deviceId, keyboardLayout, deliveryMethod, pickup, shippingAddress } =
    useReplacementDraftStore();

  const selectedOption = options.find((option) => option.id === deviceId);
  const layout =
    keyboardLayout && keyboardLayout !== NO_KEYBOARD_LAYOUT ? keyboardLayout : undefined;
  const deviceValue = selectedOption
    ? [`${selectedOption.make} ${selectedOption.model}`, layout].filter(Boolean).join(", ")
    : undefined;

  const isPickup = deliveryMethod === "pickup";
  const pickupMethodLabel = pickup
    ? getString(`${CHOOSE_DELIVERY_STRINGS}.pickupMethods.${pickup.deliveryMethod}`)
    : undefined;
  const deliveryDetail = isPickup
    ? pickup && `${pickupMethodLabel} (${pickup.buildingCode})`
    : shippingAddress && formatSavedAddress(shippingAddress);

  const rows: SummaryRow[] = [
    {
      label: getString(CONFIRMATION_STRING_KEYS.orderNumberLabel),
      value: orderResult.orderNumber,
      isEmphasised: true,
    },
    ...(deviceValue
      ? [
          {
            label: getString(CONFIRMATION_STRING_KEYS.deviceLabel),
            value: deviceValue,
            hasDividerAbove: true,
          },
        ]
      : []),
    {
      label: getString(CONFIRMATION_STRING_KEYS.deliveryMethodLabel),
      value: getString(`${CHOOSE_DELIVERY_STRINGS}.${isPickup ? "pickup" : "ship"}.title`),
      hasDividerAbove: !deviceValue,
    },
    ...(deliveryDetail
      ? [
          {
            label: getString(
              isPickup ? CONFIRMATION_STRING_KEYS.buildingLabel : CONFIRMATION_STRING_KEYS.addressLabel,
            ),
            value: deliveryDetail,
          },
        ]
      : []),
    ...(orderResult.estimatedReady
      ? [
          {
            label: getString(CONFIRMATION_STRING_KEYS.estimatedReadyLabel),
            value: orderResult.estimatedReady,
          },
        ]
      : []),
  ];

  return (
    <BorderedCard borderRadius="300" padding="S400" dataTestId="confirmation-summary">
      <SummaryRows rows={rows} />
    </BorderedCard>
  );
};
